import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  type RiskReductionMeasure,
  type RiskReductionMeasureImplementationStatus,
  useUpdateRiskReductionMeasure,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { toRiskReductionMeasureUpdateInput } from './riskReductionMeasuresUtils'

type EditRiskReductionMeasureImplementationStatusDialogProps = {
  isOpen: boolean
  onClose: () => void
  complianceId: string
  riskReductionMeasure: RiskReductionMeasure
}

export function EditRiskReductionMeasureImplementationStatusDialog({
  isOpen,
  onClose,
  complianceId,
  riskReductionMeasure,
}: EditRiskReductionMeasureImplementationStatusDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const updateMutation = useUpdateRiskReductionMeasure(complianceId)

  const [status, setStatus] = useState<RiskReductionMeasureImplementationStatus>(
    riskReductionMeasure.implementationStatus
  )

  useEffect(() => {
    if (!isOpen) {
      return
    }
    setStatus(riskReductionMeasure.implementationStatus)
  }, [isOpen, riskReductionMeasure.implementationStatus])

  const isSaving = updateMutation.isPending

  const hasChanges = useMemo(
    () => status !== riskReductionMeasure.implementationStatus,
    [riskReductionMeasure.implementationStatus, status]
  )

  const handleClose = useCallback(() => {
    if (isSaving) {
      return
    }
    onClose()
  }, [isSaving, onClose])

  const handleSubmit = () => {
    if (isSaving || !hasChanges) {
      if (!hasChanges) {
        handleClose()
      }
      return
    }

    updateMutation.mutate(
      {
        riskReductionMeasureId: riskReductionMeasure.id,
        ...toRiskReductionMeasureUpdateInput(riskReductionMeasure, {
          implementationStatus: status,
        }),
      },
      {
        onSuccess: () => {
          toast.success(t('riskReductionMeasuresLibraryPage.updateSuccess'))
          onClose()
        },
        onError: (error) => {
          toast.error(
            error.message ??
              t('riskReductionMeasuresLibraryPage.saveFailureUpdate')
          )
        },
      }
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='sm:max-w-lg' showCloseButton={!isSaving}>
        <DialogHeader>
          <DialogTitle>
            {t(
              'riskReductionMeasuresLibraryPage.rrmDetail.editImplementationStatusDialog.title'
            )}
          </DialogTitle>
        </DialogHeader>

        <div className='space-y-3'>
          <Label>
            {t('riskReductionMeasuresLibraryPage.implementation.statusLabel')}
          </Label>
          <RadioGroup
            value={status}
            onValueChange={(value) => {
              setStatus(value as RiskReductionMeasureImplementationStatus)
            }}
            disabled={isSaving}
            className='flex flex-col gap-2 sm:flex-row sm:gap-6'
          >
            <div className='flex items-center gap-2'>
              <RadioGroupItem
                value='NOT_IMPLEMENTED'
                id='rrm-edit-implementation-status-not-implemented'
              />
              <Label
                htmlFor='rrm-edit-implementation-status-not-implemented'
                className='font-normal'
              >
                {t(
                  'riskReductionMeasuresLibraryPage.implementation.status.notImplemented'
                )}
              </Label>
            </div>
            <div className='flex items-center gap-2'>
              <RadioGroupItem
                value='IMPLEMENTED'
                id='rrm-edit-implementation-status-implemented'
              />
              <Label
                htmlFor='rrm-edit-implementation-status-implemented'
                className='font-normal'
              >
                {t(
                  'riskReductionMeasuresLibraryPage.implementation.status.implemented'
                )}
              </Label>
            </div>
          </RadioGroup>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={handleClose} disabled={isSaving}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving || !hasChanges}>
            {isSaving ? <Loader2 className='size-4 animate-spin' /> : null}
            {t('riskReductionMeasuresLibraryPage.formDialog.updateButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
